import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';

import { hasAppliedClaimFreeSavings, hasDiscountableFarmRisk } from '@ourbranch/farm-utils';
import { policyTypes } from '@ourbranch/policy-types';

import { appConfig } from 'brand-config';
import { ExpandableCard } from 'core/components/expandable-card';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { track } from 'core/helpers/analytics';
import AutoBundleSelect from 'common/components/farm/auto-bundle-select';
import { AssociationDiscountModal } from 'common/components/farm/association-discount-modal';
import { DiscountRow } from './discount-row';
import { FARM_DISCOUNTS } from './farm-discounts.constants';

/**
 * Editable farm discounts for the policy screen (#32069 / #32036 §4.2, restyled in #32231).
 *
 * #32231 makes this section match the new-offer discounts card: an ExpandableCard with an
 * "active of total" badge and per-discount add/remove rows (the offer's DiscountsCard look),
 * instead of the old flat Switch list. It also fixes two reported problems:
 *   - claim-free is underwriter-only — agents can view it but only a UW (canApproveUWReview) can
 *     change it. Every other discount stays editable by any licensed editor.
 *   - toggling a discount now marks the Formik field touched, so the policy "Preview policy
 *     changes" footer actually appears (it gates on `touched`; setFieldValue alone never sets it).
 *
 * Each discount binds to a `segment.global.*` field (the same fields the offer-side farm-discount
 * component writes) — the rows and their gating live in `./farm-discounts.constants`, which is also
 * what `policy.query.fragments.test.js` checks the GraphQL selection against (#34739).
 *
 * NOTE: the IMT source carries no discount data, so nothing is auto-populated on migration — this
 * section lets staff apply discounts after. Claim-free is the exception: it is never stored as a
 * flag — it is whatever Clarion Door actually applied on the rated premium, and this card reports
 * that (#34635).
 */
const FarmDiscounts = observer(function FarmDiscounts({ disabled }) {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const { canApproveUWReview } = useSession();
  const {
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();
  const brandDiscounts = appConfig?.discounts ?? {};
  const lossHistory = values?.lossHistory ?? [];
  /**
   * Whether a claim-free discount was ACTUALLY APPLIED — the sum of Clarion Door's
   * `claim_free_savings` lines across every rated risk, so this row, the declaration and the rated
   * premium all read one number.
   *
   * Two earlier shapes were both wrong. `Boolean(values.global.claimFreeDiscount)` read the UW
   * OVERRIDE, which the IMT converter writes as null on purpose — so the row sat inactive on every
   * migrated policy whatever its claims said, and the "+" invited a UW to force the 5-year factor
   * (.90) over a correct 3-year one (.95). Deriving it from loss history (#34635) fixed that but
   * still answered "is the insured claim free" rather than "did they receive the discount"; the two
   * diverge on liability-only policies, where Clarion Door returns no property node and therefore no
   * savings line at all, and where the factor simply yields $0.
   */
  const claimFreeActive = hasAppliedClaimFreeSavings(values?.coverages);
  const entityType = values?.entity?.type;
  const isIndividualOrJoint = entityType === 'Individual' || entityType === 'Joint';
  const isFarmRental = policyStore?.policy?.policyType === policyTypes.FarmRental;

  /**
   * Add marks every loss ignored (owner decision, #34635) and clears any stale `false` override.
   * Both are RATING INPUTS: they change what the next rate produces, which is what ultimately moves
   * this row. The row itself reports the rated result, so it does not flip until a re-rate lands —
   * deliberate, since showing it on before the premium reflects it would put the card back out of
   * step with the dec, which is the whole problem being fixed.
   *
   * Remove writes the `false` override rather than un-ignoring, because un-ignoring cannot always
   * remove the discount: a policy whose only loss is six years old is claim-free by age, with no
   * ignore flag to clear, so an un-ignore would silently no-op. The override always works.
   */
  const addClaimFreeDiscount = useCallback(() => {
    setFieldValue(
      'lossHistory',
      lossHistory.map((entry) => ({ ...entry, ignore: true }))
    );
    setFieldTouched('lossHistory', true, false);
    setFieldValue('global.claimFreeDiscount', null);
    setFieldTouched('global.claimFreeDiscount', true, false);
    track('Farm Discount Toggled', { field: 'claimFreeDiscount', value: true });
  }, [lossHistory, setFieldValue, setFieldTouched]);

  /**
   * #34739 — MO Farm Associations is the one discount that is not a plain flag: the CFM rater reads
   * `farm_association` as the COUNT of the two membership fields, so setting
   * `global.farmAssociationDiscount` alone left the premium unmoved while the dec printed
   * "Included". The row therefore applies through the same modal the offer sidebar uses, and every
   * write covers all three fields together.
   */
  const [editingMemberships, setEditingMemberships] = useState(null);

  const setMembershipDiscount = useCallback(
    ({ field, memberships }, isOn, selected) => {
      setFieldValue(`global.${field}`, isOn);
      setFieldTouched(`global.${field}`, true, false);
      for (const membership of memberships) {
        // Always write every membership, not just the selected ones: an unchecked association left
        // at a stale `true` would keep rating as a membership the insured no longer has.
        setFieldValue(`global.${membership}`, isOn ? Boolean(selected?.[membership]) : false);
        setFieldTouched(`global.${membership}`, true, false);
      }
      track('Farm Discount Toggled', { field, value: isOn });
    },
    [setFieldValue, setFieldTouched]
  );

  const setDiscount = useCallback(
    (field, value) => {
      setFieldValue(`global.${field}`, value);
      // #32231: mark the field touched so the policy "Preview policy changes" footer appears (it
      // gates on Object.keys(touched).length). setFieldValue alone never marks touched, which is
      // why toggling a discount used to leave the preview button hidden. Pass shouldValidate=false
      // to avoid forcing a full-form validation pass on every toggle.
      setFieldTouched(`global.${field}`, true, false);
      track('Farm Discount Toggled', { field, value });
    },
    [setFieldValue, setFieldTouched]
  );

  // #34855 / #34854: no discount factor applies to an inland-marine-only or liability-only farm, so
  // every row is inapplicable.
  if (!hasDiscountableFarmRisk(values?.farmLocations)) {
    return null;
  }

  // Only show discounts the brand offers; mirrors the offer card (it never lists unavailable ones).
  // #32516: also drop rental-inapplicable discounts (Legacy/Military/Education) on a Rental policy.
  const visibleDiscounts = FARM_DISCOUNTS.filter(({ enabledFlag, hideForRental }) => {
    if (hideForRental && isFarmRental) {
      return false;
    }
    return enabledFlag ? Boolean(brandDiscounts[enabledFlag]) : true;
  });
  const activeCount = visibleDiscounts.filter(({ field, derived }) =>
    derived ? claimFreeActive : Boolean(values?.global?.[field])
  ).length;

  return (
    <div id="farm_discounts">
      <ExpandableCard title="Discounts" badge={`${activeCount} of ${visibleDiscounts.length}`}>
        {visibleDiscounts.map(({ field, label, entityGated, uwOnly, derived, memberships }) => {
          const isActive = derived ? claimFreeActive : Boolean(values?.global?.[field]);
          const entityEnabled = entityGated ? isIndividualOrJoint : true;
          // claim-free is UW-only (#32231); every other discount is editable by any licensed editor.
          // #34635: it also needs a loss to act on — both actions operate on `lossHistory`, so with
          // none there is nothing for either to point at and the row is locked whichever way it
          // reads. (It no longer follows that an empty loss history means the row shows applied:
          // that is now Clarion Door's answer, and a liability-only policy has neither losses nor a
          // savings line.) The fix for a policy that should not have the discount is to add the
          // missing claim, not to toggle the row.
          const canChange =
            !disabled &&
            entityEnabled &&
            (uwOnly ? canApproveUWReview : true) &&
            (derived ? lossHistory.length > 0 : true);
          // #30919: the Auto/Home bundle is a three-way choice (none / BIX / other carrier), not a
          // yes/no toggle — render the shared dropdown instead of an add/remove row. It writes both
          // global.multiPolicyDiscount and global.autoBundleCarrier and marks them touched itself.
          if (field === 'multiPolicyDiscount') {
            return <AutoBundleSelect key={field} label={label} disabled={!canChange} />;
          }
          // #34739: MO Farm Associations records WHICH associations the insured belongs to, not just
          // that the discount is on, so adding it opens the same modal the offer sidebar uses and
          // editing reopens it. Removal is still a direct write — of all three fields at once.
          const usesMembershipModal = field === 'farmAssociationDiscount';
          const openMembershipModal = () => setEditingMemberships({ field, memberships });
          const onAdd = derived
            ? addClaimFreeDiscount
            : usesMembershipModal
              ? openMembershipModal
              : () => setDiscount(field, true);
          const onRemove = usesMembershipModal
            ? () => setMembershipDiscount({ field, memberships }, false)
            : () => setDiscount(field, false);
          return (
            <DiscountRow
              key={field}
              id={field}
              label={label}
              isActive={isActive}
              disabled={disabled}
              onAdd={!isActive && canChange ? onAdd : undefined}
              onEdit={isActive && canChange && usesMembershipModal ? openMembershipModal : undefined}
              onRemove={isActive && canChange ? onRemove : undefined}
            />
          );
        })}
      </ExpandableCard>
      {editingMemberships && (
        <AssociationDiscountModal
          isActive={Boolean(values?.global?.[editingMemberships.field])}
          memberships={values?.global}
          onApply={(selected) => setMembershipDiscount(editingMemberships, true, selected)}
          onRemove={() => setMembershipDiscount(editingMemberships, false)}
          onClose={() => setEditingMemberships(null)}
        />
      )}
    </div>
  );
});

FarmDiscounts.propTypes = {
  disabled: PropTypes.bool
};

FarmDiscounts.defaultProps = {
  disabled: false
};

export default FarmDiscounts;
